/**
 * Post-trip adjustments (S129): tip and dispute. Both change the rider's final charge *after* the ride, so both
 * are recorded as signed, reason-coded adjustments in the ride currency — a **tip** adds to the charge, a
 * **dispute credit** subtracts. A dispute is a small support-path state machine (`open → under_review →
 * resolved | rejected`); resolving it with a credit yields the adjustment the caller folds into the final charge.
 */
import { money } from '@vello/core';
/** Build a tip adjustment (positive). The amount must be strictly positive. */
export function tipAdjustment(amount, atMs) {
    if (amount.amountMinor <= 0n)
        return { ok: false, reason: 'NON_POSITIVE_TIP' };
    return { ok: true, adjustment: { kind: 'tip', amount, reason: 'TIP', atMs } };
}
/** Open a dispute for a ride with a non-empty reason. */
export function openDispute(rideId, reason, atMs) {
    if (reason.trim() === '')
        return { ok: false, reason: 'EMPTY_REASON' };
    return { ok: true, dispute: { rideId, reason, state: 'open', openedAtMs: atMs } };
}
/** Move a dispute into review — only from `open`. */
export function reviewDispute(dispute) {
    if (dispute.state !== 'open')
        return { ok: false, reason: 'NOT_OPEN' };
    return { ok: true, dispute: { ...dispute, state: 'under_review' } };
}
/**
 * Resolve a dispute with a (non-negative) credit — from `open` or `under_review`. A zero credit resolves in the
 * driver/platform's favour with no adjustment; a positive credit yields a negative `PostTripAdjustment`.
 */
export function resolveDispute(dispute, credit, reasonCode, atMs) {
    if (dispute.state !== 'open' && dispute.state !== 'under_review')
        return { ok: false, reason: 'NOT_ACTIONABLE' };
    if (credit.amountMinor < 0n)
        return { ok: false, reason: 'NEGATIVE_CREDIT' };
    if (reasonCode.trim() === '')
        return { ok: false, reason: 'EMPTY_REASON' };
    const resolved = { ...dispute, state: 'resolved', resolvedAtMs: atMs };
    if (credit.amountMinor === 0n)
        return { ok: true, dispute: resolved };
    const adjustment = {
        kind: 'dispute_credit',
        amount: money(-credit.amountMinor, credit.currency),
        reason: reasonCode,
        atMs,
    };
    return { ok: true, dispute: resolved, adjustment };
}
/** Reject a dispute (no adjustment) — from `open` or `under_review`. */
export function rejectDispute(dispute, atMs) {
    if (dispute.state !== 'open' && dispute.state !== 'under_review')
        return { ok: false, reason: 'NOT_ACTIONABLE' };
    return { ok: true, dispute: { ...dispute, state: 'rejected', resolvedAtMs: atMs } };
}
/**
 * The rider's final charge = base total + the signed sum of all post-trip adjustments (tips add, dispute credits
 * subtract). Every adjustment must share the base currency — a mismatch is refused, never silently converted.
 */
export function finalCharge(baseTotal, adjustments) {
    if (adjustments.some((a) => a.amount.currency !== baseTotal.currency))
        return { ok: false, reason: 'CURRENCY_MISMATCH' };
    const sum = adjustments.reduce((acc, a) => acc + a.amount.amountMinor, baseTotal.amountMinor);
    return { ok: true, total: money(sum, baseTotal.currency) };
}
//# sourceMappingURL=adjustments.js.map